import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { SketchCanvasOverlayHtml } from './SketchCanvasOverlayHtml';
import { useSketchStore } from '../../state/useSketchStore';
import { useAppStore } from '../../state/store';
import { useDocumentStore } from '../../state/useDocumentStore';
import { gumballOwnsPointer } from '../../state/useGumballStore';
import { entityMeshRegistry } from '../../rendering/toolEventBus';
import { resolveVisibility, currentVisibilityInput } from '../../rendering/visibility';
import {
    faceSelectionId, edgeSelectionId, looseEdgeSelectionId, solidEffectiveVisible,
} from '../../core/sketch/types';
import { buildTopology } from '../../core/sketch/topology';
import { sceneSourceOf } from '../../core/scene';
import {
    setSketchMarqueeController, sketchDefaultClear, sketchDragOwnsPointer,
    type SketchMarqueeController, type SketchHit,
} from './sketchToolBus';

/**
 * Sketch select-mode marquee (window/crossing box selection).
 *
 * The select-mode gesture that was missing next to the Shift+drag push/pull
 * and the edge/vertex drags: a press-drag on EMPTY SPACE draws the classic
 * rubber rectangle and multi-selects sketch entities — faces and solid
 * edges — with the other workbenches' box contract:
 * - left-to-right drag  = WINDOW (blue): only entities fully inside select
 * - right-to-left drag  = CROSSING (green, dashed): touched entities select
 * - plain drag replaces the selection, Ctrl-drag adds, Shift-drag removes
 *   every covered entity (BIM SelectTool semantics).
 *
 * The gesture arms through the tool bus's default (select) path — a press
 * that starts on geometry keeps the click/drag contract — and tracks the
 * pointer at window level so the rect keeps updating over faces, foreign
 * references and even outside the canvas (the Zoom Window drag pattern).
 */

/** Drag must exceed this before a press becomes a marquee (BIM parity). */
const DRAG_THRESHOLD_PX = 4;

interface MarqueeRect {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    crossing: boolean;
}

interface MarqueeGesture {
    /** Press corner, canvas-LOCAL px (rect offset excluded). */
    startPx: { x: number; y: number };
    currentPx: { x: number; y: number } | null;
    dragging: boolean;
    /** Modifier snapshot from the press; the release event's own keys win. */
    shiftKey: boolean;
    ctrlKey: boolean;
}

interface ScreenBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

interface MarqueePicks {
    /** Sketch-store composites (faces + solid edges + loose edges) covered
     *  by the box. */
    sketchIds: string[];
    /** Owning solid ids for the app-store selection (Entity Info / gizmo). */
    solidIds: string[];
    /** Foreign citizens (CAD entities, BIM elements) covered by the box —
     *  they join the shared app selection (B4 edit bridge). */
    foreignIds: string[];
}

/** Project world points to canvas-LOCAL px bounds; null when any point sits
 *  behind the camera (the projected bounds would be mirrored garbage). */
function screenBoundsOfPoints(
    points: THREE.Vector3[],
    camera: THREE.Camera,
    domElement: HTMLCanvasElement
): ScreenBounds | null {
    camera.updateMatrixWorld();
    const rect = domElement.getBoundingClientRect();
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const point of points) {
        // View-space z > 0 means behind the camera plane: bounds unreliable.
        if (point.clone().applyMatrix4(camera.matrixWorldInverse).z >= 0) return null;
        const ndc = point.clone().project(camera);
        const x = ((ndc.x + 1) / 2) * rect.width;
        const y = ((1 - ndc.y) / 2) * rect.height;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
    }
    if (!Number.isFinite(minX)) return null;
    return { minX, maxX, minY, maxY };
}

const boxContains = (bounds: ScreenBounds, box: ScreenBounds): boolean =>
    bounds.minX >= box.minX && bounds.maxX <= box.maxX &&
    bounds.minY >= box.minY && bounds.maxY <= box.maxY;

const boxIntersects = (bounds: ScreenBounds, box: ScreenBounds): boolean =>
    !(bounds.maxX < box.minX || bounds.minX > box.maxX ||
        bounds.maxY < box.minY || bounds.minY > box.maxY);

/** Every selectable sketch entity the box covers: faces through their outer
 *  loop bounds, solid edges through their endpoints, drawn segments through
 *  theirs. Locked and hidden solids stay out (the click contract skips them
 *  too). */
function collectMarqueePicks(
    box: ScreenBounds,
    crossing: boolean,
    camera: THREE.Camera,
    domElement: HTMLCanvasElement
): MarqueePicks {
    const store = useSketchStore.getState();
    const sketchIds: string[] = [];
    const solidIds: string[] = [];
    const test = crossing ? boxIntersects : boxContains;

    for (const solid of Object.values(store.solids)) {
        if (solid.locked || !solidEffectiveVisible(solid, store.tags)) continue;
        let solidHit = false;

        for (const face of solid.faces) {
            const points = face.outer.map((p) => new THREE.Vector3(...p));
            if (points.length === 0) continue;
            const bounds = screenBoundsOfPoints(points, camera, domElement);
            if (!bounds) continue;
            if (test(bounds, box)) {
                sketchIds.push(faceSelectionId(solid.id, face.id));
                solidHit = true;
            }
        }

        const topology = buildTopology(solid);
        topology.edges.forEach((edge, key) => {
            const bounds = screenBoundsOfPoints([edge.a, edge.b], camera, domElement);
            if (!bounds) return;
            if (test(bounds, box)) {
                sketchIds.push(edgeSelectionId(solid.id, key));
                solidHit = true;
            }
        });

        if (solidHit) solidIds.push(solid.id);
    }

    // Drawn segments are first-class marquee targets (their endpoints frame
    // them): a covered segment joins the sketch selection; there is no
    // app-store entity to mirror.
    for (const edge of Object.values(store.edges)) {
        const bounds = screenBoundsOfPoints(
            [new THREE.Vector3(...edge.a), new THREE.Vector3(...edge.b)],
            camera,
            domElement
        );
        if (!bounds) continue;
        if (test(bounds, box)) sketchIds.push(looseEdgeSelectionId(edge.id));
    }

    // Foreign citizens (CAD entities, BIM elements) join the shared app
    // selection the same way their click contract does: registry roots give
    // world bounds, the ledger's visibility/lock language filters them.
    const foreignIds: string[] = [];
    const picked = new Set<string>(solidIds);
    const ledger = useDocumentStore.getState().sceneObjects;
    const visInput = currentVisibilityInput();
    entityMeshRegistry.forEach((obj, id) => {
        if (picked.has(id)) return;
        const source = sceneSourceOf(id);
        if (!source || source === 'sketch') return;
        const sceneObject = ledger[id];
        if (sceneObject) {
            const r = resolveVisibility(sceneObject, visInput);
            if (!r.visible || r.locked) return;
        }
        const worldBox = new THREE.Box3().setFromObject(obj);
        if (worldBox.isEmpty()) return;
        const bounds = screenBoundsOfPoints(boxCorners(worldBox), camera, domElement);
        if (!bounds) return;
        if (test(bounds, box)) {
            foreignIds.push(id);
            picked.add(id);
        }
    });

    return { sketchIds, solidIds, foreignIds };
}

/** The eight corners of a world-space box. */
function boxCorners(box: THREE.Box3): THREE.Vector3[] {
    const corners: THREE.Vector3[] = [];
    for (const x of [box.min.x, box.max.x]) {
        for (const y of [box.min.y, box.max.y]) {
            for (const z of [box.min.z, box.max.z]) {
                corners.push(new THREE.Vector3(x, y, z));
            }
        }
    }
    return corners;
}

/** Select-mode marquee gesture: bus arming + window-level pointer tracking.
 *  Returns the live rubber rect (canvas-local px) while a marquee drags. */
export function useSketchMarqueeSelect(): MarqueeRect | null {
    const camera = useThree((s) => s.camera);
    const gl = useThree((s) => s.gl);
    const [rect, setRect] = useState<MarqueeRect | null>(null);
    // Imperative gesture state: the bus callback must never read a stale
    // closure, and the window-level release listener must not depend on
    // React state.
    const rigRef = useRef({ camera, gl });
    rigRef.current = { camera, gl };
    const gestureRef = useRef<MarqueeGesture | null>(null);

    const resetGesture = useCallback((): MarqueeGesture | null => {
        const gesture = gestureRef.current;
        gestureRef.current = null;
        setRect(null);
        return gesture;
    }, []);

    const endGesture = useCallback((commit: boolean, release?: PointerEvent): void => {
        const gesture = resetGesture();
        if (!gesture) return;
        if (!commit || !gesture.dragging || !gesture.currentPx) {
            if (commit && !gesture.dragging) {
                // Clean press-release on empty space: the plain select/clear
                // contract (deferred like the bus default so a press that
                // started a gizmo drag never wipes the selection).
                if (!gumballOwnsPointer()) sketchDefaultClear();
            }
            return;
        }
        // Drag direction picks the box mode: right-to-left = crossing.
        const crossing = gesture.currentPx.x < gesture.startPx.x;
        const box: ScreenBounds = {
            minX: Math.min(gesture.startPx.x, gesture.currentPx.x),
            maxX: Math.max(gesture.startPx.x, gesture.currentPx.x),
            minY: Math.min(gesture.startPx.y, gesture.currentPx.y),
            maxY: Math.max(gesture.startPx.y, gesture.currentPx.y),
        };
        const { gl: renderer, camera: cam } = rigRef.current;
        const picks = collectMarqueePicks(box, crossing, cam, renderer.domElement);

        // Modifier semantics of the other workbenches' select: Shift is the
        // dominant "remove covered" key, Ctrl (or Cmd) extends the selection
        // set, a plain box replaces it (Ctrl+Shift keeps the Shift behavior).
        const shift = release ? release.shiftKey : gesture.shiftKey;
        const extend = !shift && (release
            ? (release.ctrlKey || release.metaKey)
            : gesture.ctrlKey);
        const sketch = useSketchStore.getState();
        const app = useAppStore.getState();
        // Foreign citizens ride the app selection alongside the owning solid
        // ids (one selection space, B4): Shift removes every covered id,
        // Ctrl extends, a plain box replaces.
        const foreignAppIds = [...picks.solidIds, ...picks.foreignIds];
        if (shift) {
            sketch.select(sketch.selectedIds.filter((id) => !picks.sketchIds.includes(id)));
            app.select(app.selectedIds.filter((id) => !foreignAppIds.includes(id)));
        } else if (extend) {
            sketch.select([...new Set([...sketch.selectedIds, ...picks.sketchIds])]);
            app.select([...new Set([...app.selectedIds, ...foreignAppIds])]);
        } else {
            sketch.select(picks.sketchIds);
            app.select(foreignAppIds);
        }
    }, [resetGesture]);

    // Bus arming: a press on the event plane (empty space) becomes a marquee
    // candidate. Geometry presses never reach here (their layers stop the
    // event), so the click/drag contract is untouched. An edit drag that
    // already owns the pointer (face/edge/vertex move) stands the marquee
    // down: one pointer gesture at a time, never a box under an edit.
    useEffect(() => {
        const controller: SketchMarqueeController = {
            begin: (hit: SketchHit): boolean => {
                if (!hit.pointerPx) return false;
                if (sketchDragOwnsPointer()) return false;
                if (gestureRef.current) return true; // already armed: swallow
                const domRect = rigRef.current.gl.domElement.getBoundingClientRect();
                gestureRef.current = {
                    startPx: { x: hit.pointerPx.x - domRect.left, y: hit.pointerPx.y - domRect.top },
                    currentPx: null,
                    dragging: false,
                    shiftKey: hit.shiftKey,
                    ctrlKey: hit.ctrlKey,
                };
                // The gizmo's own pointerdown listener runs after the bus
                // dispatch: a press that starts a gizmo drag stands the
                // marquee down (same one-tick defer as the default clear).
                window.setTimeout(() => {
                    if (gumballOwnsPointer()) gestureRef.current = null;
                }, 0);
                return true;
            },
            // The bus calls this when an edit drag begins while a marquee
            // candidate is still armed: stand down without committing.
            cancel: (): void => endGesture(false),
        };
        setSketchMarqueeController(controller);
        return () => setSketchMarqueeController(null);
    }, [endGesture]);

    // Window-level tracking: the rubber rect keeps updating over faces,
    // foreign references and even outside the canvas; release commits.
    useEffect(() => {
        const onMove = (e: PointerEvent): void => {
            const gesture = gestureRef.current;
            if (!gesture) return;
            const domRect = rigRef.current.gl.domElement.getBoundingClientRect();
            gesture.currentPx = { x: e.clientX - domRect.left, y: e.clientY - domRect.top };
            if (!gesture.dragging) {
                const dx = gesture.currentPx.x - gesture.startPx.x;
                const dy = gesture.currentPx.y - gesture.startPx.y;
                if (dx * dx + dy * dy <= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
                gesture.dragging = true;
            }
            setRect({
                x0: Math.min(gesture.startPx.x, gesture.currentPx.x),
                y0: Math.min(gesture.startPx.y, gesture.currentPx.y),
                x1: Math.max(gesture.startPx.x, gesture.currentPx.x),
                y1: Math.max(gesture.startPx.y, gesture.currentPx.y),
                crossing: gesture.currentPx.x < gesture.startPx.x,
            });
        };
        const onUp = (e: PointerEvent): void => {
            if (!gestureRef.current) return;
            endGesture(e.button === 0, e);
        };
        const onCancel = (): void => endGesture(false);
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') endGesture(false);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('blur', onCancel);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('blur', onCancel);
            window.removeEventListener('keydown', onKey);
        };
    }, [endGesture]);

    return rect;
}

/** The live rubber rectangle, drawn with the shared selection-box classes
 *  (blue window / dashed green crossing — the other workbenches' look). */
export function SketchMarqueeScope() {
    const rect = useSketchMarqueeSelect();
    if (!rect) return null;
    return (
        <SketchCanvasOverlayHtml>
            <div
                className={`selection-box ${rect.crossing ? 'selection-box--crossing' : 'selection-box--window'}`}
                style={{
                    left: `${rect.x0}px`,
                    top: `${rect.y0}px`,
                    width: `${rect.x1 - rect.x0}px`,
                    height: `${rect.y1 - rect.y0}px`,
                }}
            >
                <div className={rect.crossing ? 'selection-box__label selection-box__label--crossing' : 'selection-box__label selection-box__label--window'}>
                    {rect.crossing ? 'Crossing' : 'Window'}
                </div>
            </div>
        </SketchCanvasOverlayHtml>
    );
}
